import { supabase } from '../../lib/supabase';
import { fetchAllPages } from '../../lib/supabasePaging';
import {
  toAccountFederation,
  toAccountFederationSource,
  type CRMAccount,
  type CRMContact,
} from '../../types/crm';
import type { CRMState, CRMAccountWithDeletedAt } from './types';
import { filterActiveAccounts } from './helpers';
import type { Database, Json } from '../../types/supabase';

type Set = (partial: Partial<CRMState> | ((state: CRMState) => Partial<CRMState>)) => void;
type Get = () => CRMState;

type AccountRow = Database['public']['Tables']['crm_accounts']['Row'];
type ContactRow = Database['public']['Tables']['crm_contacts']['Row'];
// Columns not yet in the generated types: soft-delete + autocariste profile fields.
type AccountRowWithDeletedAt = AccountRow & {
  deleted_at?: string | null;
  region?: string | null;
  fleet_size?: number | null;
  fleet_light_vehicles?: number | null;
  annual_revenue_eur?: number | null;
  revenue_year?: number | null;
  net_result_eur?: number | null;
  tms?: string | null;
  groupement?: string | null;
  federation?: string | null;
  federation_source?: string | null;
  federation_checked_at?: string | null;
  data_source?: string | null;
};

interface AccountRoleRow {
  account_id: string;
  role: 'client' | 'former_client' | 'prospect';
  source: 'stripe' | 'invoice' | null;
  invoice_cadence: string | null;
  next_renewal_at: string | null;
}

/** Shape returned by the `delete_account_cascade` RPC (typed `Json` in the DB). */
interface DeleteAccountCascadeResult {
  success: boolean;
  account_name?: string;
  error?: string;
  message?: string;
  dependencies?: {
    contacts?: number;
    opportunities?: number;
    meetings?: number;
    notes?: number;
    appointments?: number;
  };
}

/** Narrow a nullable custom_fields JSON into the domain Record shape. */
function toRecord(value: Json | null | undefined): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Normalize a `crm_accounts` DB row into the domain `CRMAccount`.
 * The commercial `role`/`status` are derived from the `v_account_role` view and
 * default to the neutral `prospect` when no revenue context is available; the
 * caller overrides them when it has fetched the role rows.
 */
function toCRMAccount(row: AccountRowWithDeletedAt): CRMAccountWithDeletedAt {
  return {
    id: row.id,
    company_id: row.company_id,
    name: row.name,
    industry: row.industry,
    website: row.website,
    logo_url: row.logo_url,
    address: row.address,
    city: row.city,
    postal_code: row.postal_code,
    country: row.country,
    siret: row.siret,
    siren: row.siren,
    naf_code: row.naf_code,
    phone: row.phone,
    role: 'prospect',
    status: 'prospect',
    notes: row.notes,
    owner_id: row.owner_id,
    parent_account_id: row.parent_account_id,
    custom_fields: toRecord(row.custom_fields),
    lat: row.lat,
    lng: row.lng,
    geocoded_at: row.geocoded_at,
    region: row.region ?? null,
    fleet_size: row.fleet_size ?? null,
    fleet_light_vehicles: row.fleet_light_vehicles ?? null,
    annual_revenue_eur: row.annual_revenue_eur ?? null,
    revenue_year: row.revenue_year ?? null,
    net_result_eur: row.net_result_eur ?? null,
    tms: row.tms ?? null,
    groupement: row.groupement ?? null,
    federation: toAccountFederation(row.federation),
    federation_source: toAccountFederationSource(row.federation_source),
    federation_checked_at: row.federation_checked_at ?? null,
    data_source: row.data_source ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    deleted_at: row.deleted_at ?? null,
  };
}

/** Normalize a `crm_contacts` insert row into the domain `CRMContact`. */
function toCRMContact(row: ContactRow): CRMContact {
  return {
    id: row.id,
    account_id: row.account_id,
    first_name: row.first_name,
    last_name: row.last_name,
    email: row.email,
    phone: row.phone,
    title: row.title,
    status: row.status === 'inactive' ? 'inactive' : 'active',
    owner_id: row.owner_id,
    last_contacted_at: row.last_contacted_at,
    feature_notifications_enabled: row.feature_notifications_enabled ?? false,
    custom_fields: toRecord(row.custom_fields),
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export const createAccountsSlice = (set: Set, get: Get) => ({
  accounts: [] as CRMAccount[],

  fetchAccounts: async () => {
    try {
      // Tous les comptes (800+ au 25/09/2026) : une réponse s'arrête à max_rows = 1000 sans erreur.
      const data = await fetchAllPages((from, to) =>
        supabase.from('crm_accounts').select('*').order('created_at', { ascending: false }).order('id').range(from, to));
      // Normalize raw rows into domain accounts (defaults role/status to prospect,
      // narrows custom_fields), then drop soft-deleted ones.
      const normalized = ((data ?? []) as AccountRowWithDeletedAt[]).map(toCRMAccount);
      const active = filterActiveAccounts(normalized);

      // Le compte n'a pas de statut commercial propre : on fusionne le RÔLE dérivé du
      // revenu depuis la vue canonique `v_account_role` (source unique).
      const { data: roleRows } = await supabase
        .from('v_account_role')
        .select('account_id, role, source, invoice_cadence, next_renewal_at');
      const roleById = new Map(
        ((roleRows ?? []) as AccountRoleRow[]).map((r) => [r.account_id, r]),
      );
      const roleToStatus = (role: string): CRMAccount['status'] =>
        role === 'client' ? 'customer' : role === 'former_client' ? 'churned' : 'prospect';
      const withRole = active.map((a) => {
        const role = (roleById.get(a.id)?.role ?? 'prospect') as CRMAccount['role'];
        const r = roleById.get(a.id);
        return {
          ...a,
          role,
          // Statut de COMPAT dérivé du rôle (la colonne DB est supprimée).
          status: roleToStatus(role),
          clientSource: r?.source ?? null,
          invoiceCadence: r?.invoice_cadence ?? null,
          nextRenewalAt: r?.next_renewal_at ?? null,
        };
      });
      set({ accounts: withRole });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch accounts';
      set({ error: message });
    }
  },

  createAccount: async (data: Partial<CRMAccount>) => {
    set({ loading: true });
    try {
      const { data: newAccount, error } = await supabase
        .from('crm_accounts')
        .insert({
          name: data.name || '',
          industry: data.industry,
          website: data.website,
          address: data.address,
          city: data.city,
          postal_code: data.postal_code,
          country: data.country,
          phone: data.phone,
          notes: data.notes,
          siret: data.siret,
          siren: data.siren,
          naf_code: data.naf_code,
          parent_account_id: data.parent_account_id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      await get().fetchAccounts();
      set({ loading: false });
      return toCRMAccount(newAccount);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create account';
      set({ error: message, loading: false });
      return null;
    }
  },

  createAccountWithContact: async (accountData: Partial<CRMAccount>, contactData: Partial<CRMContact>) => {
    set({ loading: true });
    try {
      // 1. Créer le compte (crm_accounts)
      const { data: newAccount, error: accountError } = await supabase
        .from('crm_accounts')
        .insert({
          name: accountData.name || '',
          industry: accountData.industry,
          website: accountData.website,
          address: accountData.address,
          city: accountData.city,
          postal_code: accountData.postal_code,
          country: accountData.country,
          phone: accountData.phone,
          notes: accountData.notes,
          siret: accountData.siret,
          siren: accountData.siren,
          naf_code: accountData.naf_code,
          parent_account_id: accountData.parent_account_id ?? null,
        })
        .select()
        .single();

      if (accountError) throw accountError;

      let newContact: CRMContact | null = null;

      // 2. Si des données de contact sont fournies, créer le contact
      if (contactData.first_name || contactData.last_name || contactData.email) {
        const { data: contact, error: contactError } = await supabase
          .from('crm_contacts')
          .insert({
            account_id: newAccount.id,
            first_name: contactData.first_name || '',
            last_name: contactData.last_name || '',
            email: contactData.email,
            phone: contactData.phone,
            title: contactData.title,
            status: contactData.status || 'active',
          })
          .select()
          .single();

        if (contactError) {
          console.error('Error creating contact:', contactError);
          // Ne pas échouer complètement si le contact n'a pas pu être créé
        } else {
          newContact = toCRMContact(contact);
        }
      }

      // 3. Rafraîchir les données
      await get().fetchAccounts();
      await get().fetchContacts();
      set({ loading: false });

      return { account: toCRMAccount(newAccount), contact: newContact };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create account with contact';
      set({ error: message, loading: false });
      return null;
    }
  },

  updateAccount: async (id: string, data: Partial<CRMAccount>) => {
    set({ loading: true });
    try {
      // Strip fields that don't exist on the DB row (role/status and friends are
      // derived from v_account_role at fetch time) and normalize custom_fields.
      const {
        role: _role,
        status: _status,
        clientSource: _clientSource,
        invoiceCadence: _invoiceCadence,
        nextRenewalAt: _nextRenewalAt,
        custom_fields,
        ...rest
      } = data;
      void _role; void _status; void _clientSource; void _invoiceCadence; void _nextRenewalAt;
      // `rest` may carry the autocariste-profile columns (region, fleet_size, tms…)
      // not yet in the generated Update type → assert instead of annotate.
      const payload = {
        ...rest,
        ...(custom_fields !== undefined ? { custom_fields: custom_fields as Json } : {}),
        updated_at: new Date().toISOString(),
      } as Database['public']['Tables']['crm_accounts']['Update'];
      const { error } = await supabase
        .from('crm_accounts')
        .update(payload)
        .eq('id', id);

      if (error) throw error;
      await get().fetchAccounts();
      set({ loading: false });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update account';
      set({ error: message, loading: false });
    }
  },

  bulkSetAccountParent: async (accountIds: string[], parentId: string) => {
    set({ loading: true });
    try {
      // Défense anti-cycle (le trigger DB reste la dernière ligne) : on ne rattache
      // jamais le parent à lui-même ni un de ses ancêtres. Un seul UPDATE ...IN étant
      // atomique, une seule ligne en violation ferait échouer tout le lot.
      const { data: ancestors } = await supabase.rpc('get_account_ancestors', {
        leaf_id: parentId,
        max_depth: 10,
      });
      const forbidden = new Set<string>([parentId]);
      for (const row of (ancestors ?? []) as Array<{ id: string }>) forbidden.add(row.id);
      const targets = accountIds.filter((id) => !forbidden.has(id));

      if (targets.length === 0) {
        set({ loading: false });
        return { success: false, error: 'Aucun compte valide à rattacher.', count: 0 };
      }

      const { error } = await supabase
        .from('crm_accounts')
        .update({ parent_account_id: parentId, updated_at: new Date().toISOString() })
        .in('id', targets);

      if (error) throw error;
      await get().fetchAccounts();
      set({ loading: false });
      return { success: true, count: targets.length };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to attach accounts to group';
      set({ error: message, loading: false });
      return { success: false, error: message, count: 0 };
    }
  },

  deleteAccount: async (id: string, force: boolean = false) => {
    try {
      // Use the cascade delete function for safer deletion
      const { data, error } = await supabase
        .rpc('delete_account_cascade', {
          p_account_id: id,
          p_force: force
        });

      if (error) {
        // Fallback to direct delete if RPC doesn't exist
        if (error.message.includes('function') || error.code === '42883') {
          const { error: deleteError } = await supabase
            .from('crm_accounts')
            .delete()
            .eq('id', id);

          if (deleteError) {
            // Check for foreign key violation
            if (deleteError.code === '23503' || deleteError.message.includes('violates foreign key')) {
              return {
                success: false,
                error: 'Impossible de supprimer ce compte : il contient des contacts, opportunités ou autres données liées. Supprimez d\'abord ces éléments ou utilisez la suppression en cascade.',
                hasDepencies: true
              };
            }
            return { success: false, error: deleteError.message };
          }
          await get().fetchAccounts();
          return { success: true };
        }
        return { success: false, error: error.message };
      }

      // The RPC returns an untyped Json blob; narrow it to the known result shape.
      const result = (data ?? null) as DeleteAccountCascadeResult | null;

      // Check RPC result
      if (result && !result.success) {
        if (result.dependencies) {
          const deps = result.dependencies;
          const depsList = [];
          if ((deps.contacts ?? 0) > 0) depsList.push(`${deps.contacts} contact(s)`);
          if ((deps.opportunities ?? 0) > 0) depsList.push(`${deps.opportunities} opportunité(s)`);
          if ((deps.meetings ?? 0) > 0) depsList.push(`${deps.meetings} réunion(s)`);
          if ((deps.notes ?? 0) > 0) depsList.push(`${deps.notes} note(s)`);
          if ((deps.appointments ?? 0) > 0) depsList.push(`${deps.appointments} rendez-vous`);

          return {
            success: false,
            error: `Impossible de supprimer "${result.account_name}" : ${depsList.join(', ')} associé(s).`,
            hasDependencies: true
          };
        }
        return { success: false, error: result.error };
      }

      await get().fetchAccounts();
      await get().fetchContacts();
      return { success: true };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Échec de la suppression du compte';
      return { success: false, error: message };
    }
  },
});
